<template>
  <span class="apr">
    <button type="button" class="apr__chip" :class="{ 'apr__chip--hot': needCount }" :aria-expanded="open ? 'true' : 'false'"
            aria-controls="apr-pane" title="승인 대기 목록 — 링크를 놓친 요청도 여기서 승인·반려해요" @click="open = !open">
      <i class="fas fa-check-double mr-1"></i>승인 대기<b v-if="needCount">{{ needCount }}</b>
    </button>
    <b-sidebar id="apr-pane" v-model="open" right shadow backdrop title="승인 대기" width="520px" bg-variant="white" @shown="load" @hidden="focusId = ''">
      <div class="apr__in">
        <p class="apr__note">에이전트가 요청한 쓰기 작업이에요. 링크를 놓쳐도 24시간 동안 여기서 다시 보고 승인하거나 반려할 수 있어요. 승인하면 10분 안에 에이전트가 이어서 진행해요.</p>
        <p v-if="loading" class="apr__note">불러오는 중이에요…</p>
        <p v-else-if="error" class="apr__err">{{ error }}</p>
        <p v-else-if="!items.length" class="apr__note">승인을 기다리는 요청이 없어요.</p>
        <p v-if="partial" class="apr__err">일부 목록을 불러오지 못했어요. 잠시 뒤 다시 열어 주세요.</p>
        <!-- 일괄 승인 — 서버 일괄 API 가 없어 단건 승인 주소를 차례로 부른다(확인 1번) -->
        <div v-if="needCount" class="apr__bulk">
          <b-button size="sm" variant="outline-primary" :disabled="!!busy || !checkedIds.length" @click="bulkApprove(false)">선택 승인 ({{ checkedIds.length }})</b-button>
          <b-button size="sm" variant="primary" :disabled="!!busy" @click="bulkApprove(true)">전체 승인 ({{ needCount }})</b-button>
        </div>
        <p v-if="busy === 'bulk'" class="apr__msg" role="status">{{ bulkDone }}/{{ bulkTotal }} 처리 중…</p>
        <p v-else-if="bulkMsg" class="apr__msg">{{ bulkMsg }}</p>
        <ul class="apr__list">
          <li v-for="it in items" :key="it.id" :ref="'apr-' + it.id" class="apr__card" :class="{ 'apr__card--focus': focusId === it.id }">
            <div class="apr__head">
              <input v-if="isNeed(it)" type="checkbox" class="apr__check" :checked="checked[it.id] === true" :disabled="!!busy"
                     :aria-label="(it.plan.title || it.tool) + ' 선택'" @change="$set(checked, it.id, $event.target.checked)">
              <span class="apr__src">{{ it.sourceLabel }}</span>
              <span class="apr__badge" :class="'apr__badge--' + it.status">{{ statusText(it) }}</span>
              <span v-if="it.autoApproved" class="apr__badge apr__badge--auto" title="사장님이 정해 둔 승인 방식에 따라 자동 승인됐어요">자동 승인</span>
            </div>
            <div class="apr__title">{{ it.plan.title || it.tool }}</div>
            <div v-if="it.plan.sellerId" class="apr__meta">상대 판매자 <b>{{ it.plan.sellerId }}</b><span v-if="it.plan.offerId"> · 상품 {{ it.plan.offerId }}</span></div>
            <template v-if="it.plan.textZh">
              <div class="apr__label">판매자에게 그대로 나갈 중국어 전문</div>
              <pre class="apr__zh">{{ it.plan.textZh }}</pre>
              <div v-if="it.plan.meaningKo" class="apr__label">참고용 한국어 뜻 (나가는 것은 위 중국어예요)</div>
              <pre v-if="it.plan.meaningKo" class="apr__ko">{{ it.plan.meaningKo }}</pre>
            </template>
            <div class="apr__time">{{ timeText(it) }}</div>
            <div class="apr__act">
              <b-button v-if="it.status !== 'approved'" size="sm" variant="primary" :disabled="busy === it.id || busy === 'bulk'" @click="decide(it, 'approve')">
                {{ it.status === 'reapprove' ? '다시 승인' : '승인' }}
              </b-button>
              <b-button v-if="it.actions.reject" size="sm" variant="outline-danger" :disabled="busy === it.id || busy === 'bulk'" @click="decide(it, 'reject')">반려</b-button>
              <span v-if="it.status === 'approved'" class="apr__hint">승인됨 — 에이전트가 이어서 진행할 거예요</span>
            </div>
            <p v-if="msg[it.id]" class="apr__msg">{{ msg[it.id] }}</p>
          </li>
        </ul>
        <p class="apr__fine">판매자 채팅으로 나간 글은 되돌릴 수 없어요. 본문을 끝까지 읽고 눌러 주세요.</p>
      </div>
    </b-sidebar>
  </span>
</template>

<script>
// 소싱관리 «승인 대기» — GET /api/approvals/pending (쿠팡 쓰기 + 1688 보내기). 승인/반려는 각 승인 라우트(브라우저 출처 검사)로 보낸다.
import axios from 'axios'

export default {
  name: 'ApprovalPendingPanel',
  data () {
    return { open: false, loading: false, error: '', partial: false, items: [], busy: '', msg: {}, timer: null, checked: {}, bulkMsg: '', focusId: '', bulkDone: 0, bulkTotal: 0 }
  },
  computed: {
    // 사람이 눌러야 하는 것(승인 전 + 사용 시간이 지난 것)
    needCount () {
      return this.items.filter(x => this.isNeed(x)).length
    },
    checkedIds () {
      return this.items.filter(x => this.isNeed(x) && this.checked[x.id] === true).map(x => x.id)
    }
  },
  mounted () {
    this.load(true)
    this.timer = setInterval(() => { if (!document.hidden) this.load(true) }, 60000)
  },
  beforeDestroy () {
    clearInterval(this.timer)
  },
  methods: {
    async load (quiet) {
      if (quiet !== true) this.loading = true
      try {
        const res = await axios.get('/api/approvals/pending')
        if (res.data && res.data.ok) {
          this.items = res.data.items || []
          // 소싱관리 표가 «등록 대기» 행 옆에 승인 상태를 보이려고 읽는다(부모가 받기만 함)
          this.$emit('loaded', this.items)
          this.partial = !!res.data.partial
          this.error = ''
        } else {
          this.error = (res.data && res.data.message) || '목록을 불러오지 못했어요.'
        }
      } catch (e) {
        console.error('[ApprovalPendingPanel] 승인 대기 목록 읽기 실패', e)
        if (quiet !== true) this.error = '목록을 불러오지 못했어요. 로그인 상태를 확인해 주세요.'
      } finally {
        this.loading = false
      }
    },
    isNeed (it) {
      return it.status === 'pending' || it.status === 'reapprove'
    },
    // 소싱관리 표에서 부른다 — 시트를 열고 그 건을 강조·스크롤
    openFor (id) {
      this.focusId = id || ''
      this.open = true
      this.$nextTick(() => {
        const el = this.$refs['apr-' + id]
        const node = Array.isArray(el) ? el[0] : el
        if (node && node.scrollIntoView) node.scrollIntoView({ block: 'center' })
      })
    },
    // 일괄 승인 — 단건 승인 주소를 차례로 부른다. 확인 1번, 결과 «N건 승인·M건 실패»
    async bulkApprove (all) {
      const targets = this.items.filter(x => this.isNeed(x) && x.actions && x.actions.approve && (all || this.checked[x.id] === true))
      if (!targets.length) return
      if (!confirm(`승인 대기 ${targets.length}건을 한꺼번에 승인할까요?
쿠팡 반영·판매자 메시지는 되돌릴 수 없어요. 내용을 먼저 확인해 주세요.`)) return
      this.busy = 'bulk'
      this.bulkMsg = ''
      this.bulkDone = 0
      this.bulkTotal = targets.length
      let ok = 0
      let fail = 0
      for (const it of targets) {
        try {
          const res = await axios.post(it.actions.approve, {})
          const d = res.data || {}
          if (d.ok === false) {
            fail += 1
            this.$set(this.msg, it.id, d.message || '승인하지 못했어요.')
          } else {
            ok += 1
            this.$set(this.msg, it.id, '')
          }
        } catch (e) {
          console.error('[ApprovalPendingPanel] 일괄 승인 중 한 건 실패', it.id, e)
          fail += 1
          const d = e && e.response && e.response.data
          this.$set(this.msg, it.id, (d && d.message) || '승인하지 못했어요.')
        }
        this.bulkDone += 1
      }
      this.checked = {}
      this.bulkMsg = `${ok}건 승인 · ${fail}건 실패` + (ok ? ' — 에이전트에게 이어서 진행하라고 알려 주세요.' : '')
      this.busy = ''
      await this.load(true)
    },
    statusText (it) {
      if (it.status === 'pending') return '승인 전'
      if (it.status === 'approved') return '승인됨'
      return '사용 시간 지남 · 다시 승인 필요'
    },
    timeText (it) {
      const ms = new Date(it.expiresAt).getTime() - Date.now()
      if (!isFinite(ms)) return ''
      if (it.status === 'approved') return ms > 0 ? `에이전트가 ${Math.max(1, Math.ceil(ms / 60000))}분 안에 이어서 진행해야 해요` : ''
      if (ms <= 0) return ''
      const h = Math.floor(ms / 3600000)
      return h >= 1 ? `대기 ${h}시간 남음` : `대기 ${Math.max(1, Math.ceil(ms / 60000))}분 남음`
    },
    async decide (it, kind) {
      const url = kind === 'reject' ? it.actions.reject : it.actions.approve
      if (!url) return
      this.busy = it.id
      this.bulkMsg = ''
      this.$set(this.msg, it.id, '')
      try {
        const res = await axios.post(url, {})
        const d = res.data || {}
        this.$set(this.msg, it.id, d.message || (kind === 'reject' ? '반려했어요.' : '승인했어요. 에이전트에게 이어서 진행하라고 알려 주세요.'))
      } catch (e) {
        console.error('[ApprovalPendingPanel] 승인/반려 실패', it.id, e)
        const d = e && e.response && e.response.data
        this.$set(this.msg, it.id, (d && d.message) || '처리하지 못했어요. 잠시 뒤 다시 시도해 주세요.')
      } finally {
        this.busy = ''
        await this.load(true)
      }
    }
  }
}
</script>

<style scoped>
.apr { display: inline-flex; }
.apr__chip { display: inline-flex; align-items: center; padding: 3px 10px; white-space: nowrap; font-size: 11.5px; font-weight: 600; color: #555; background: #fff; border: 1px solid #dfe3e8; border-radius: 6px; cursor: pointer; }
.apr__chip:hover { border-color: #888; color: #222; }
.apr__chip--hot { border-color: #e8590c; color: #e8590c; }
.apr__chip b { margin-left: 6px; min-width: 18px; padding: 0 5px; border-radius: 9px; background: #e8590c; color: #fff; font-size: 11px; text-align: center; }
.apr__in { padding: 12px 16px 24px; }
.apr__note { font-size: 12.5px; color: #666; margin-bottom: 10px; }
.apr__err { font-size: 12.5px; color: #c92a2a; margin-bottom: 10px; }
.apr__list { list-style: none; margin: 0; padding: 0; }
.apr__card { border: 1px solid #e3e6ea; border-radius: 8px; padding: 12px; margin-bottom: 12px; }
.apr__card--focus { border-color: #e8590c; box-shadow: 0 0 0 2px rgba(232, 89, 12, .15); }
.apr__check { margin-right: 2px; cursor: pointer; }
.apr__bulk { display: flex; gap: 6px; margin-bottom: 10px; }
.apr__head { display: flex; gap: 6px; align-items: center; margin-bottom: 6px; flex-wrap: wrap; }
.apr__src { font-size: 11.5px; font-weight: 700; color: #444; }
.apr__badge { font-size: 11px; padding: 1px 7px; border-radius: 9px; background: #f1f3f5; color: #555; }
.apr__badge--pending { background: #fff4e6; color: #d9480f; }
.apr__badge--approved { background: #ebfbee; color: #2b8a3e; }
.apr__badge--reapprove { background: #fff0f0; color: #c92a2a; }
.apr__badge--auto { background: #e7f5ff; color: #1971c2; }
.apr__title { font-size: 14px; font-weight: 700; margin-bottom: 4px; }
.apr__meta { font-size: 12px; color: #666; margin-bottom: 6px; }
.apr__label { font-size: 11.5px; color: #666; margin: 8px 0 3px; }
.apr__zh, .apr__ko { white-space: pre-wrap; word-break: break-word; background: #f8f9fa; border-radius: 6px; padding: 8px 10px; font-size: 13px; margin: 0; max-height: 260px; overflow: auto; }
.apr__ko { color: #666; font-size: 12px; }
.apr__time { font-size: 11.5px; color: #868e96; margin-top: 8px; }
.apr__act { display: flex; gap: 6px; align-items: center; margin-top: 8px; }
.apr__hint { font-size: 12px; color: #2b8a3e; }
.apr__msg { font-size: 12px; color: #1971c2; margin: 6px 0 0; }
.apr__fine { font-size: 11.5px; color: #868e96; }
</style>
